import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import useCreditWarning from '../../../hooks/useCreditWarning';
import CreditWarningModal from '../../common/CreditWarningModal';
import { useCampaignComplianceStatus } from '../../../hooks/useCampaignComplianceStatus';
import {
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  Button,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
} from '@chakra-ui/react';
import { FiX } from 'react-icons/fi';
import { LuChevronDown, LuChevronLeft, LuSend, LuPlus, LuMoreVertical, LuArrowLeftRight, LuShieldCheck, LuTrash2, LuList, LuPause, LuPlay } from 'react-icons/lu';
import { GmailLogo, OutlookLogo } from './EmailProviderLogos';

const SEQUENCERS = [
  { id: 'gillyreach', name: 'GillyReach', icon: '/gillyreach-favicon.png', available: true },
];

const DEFAULT_FOLLOWUP_DELAY = 3;
const MIN_FOLLOWUP_DELAY     = 1;
const MAX_FOLLOWUP_DELAY     = 90;

// Shared style tokens matching BillingPage
const primaryBtn = {
  bg: '#fafafa',
  color: '#0a0a0a',
  borderRadius: 'full',
  fontWeight: '600',
  fontSize: '13px',
  h: '32px',
  px: 4,
  _hover: { opacity: 0.85 },
  _disabled: { opacity: 0.3, cursor: 'not-allowed' },
  _focus: { boxShadow: 'none', outline: 'none' },
};

const outlineBtn = {
  bg: 'transparent',
  borderRadius: 'full',
  fontWeight: '500',
  fontSize: '13px',
  h: '32px',
  px: 4,
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'rgba(255,255,255,0.6)',
  _hover: { borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.9)', bg: 'rgba(255,255,255,0.04)' },
  _focus: { boxShadow: 'none', outline: 'none' },
};

const menuListStyles = {
  bg: '#111111',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: '10px',
  py: 1,
  px: 1,
  minW: '200px',
  shadow: '0 8px 24px rgba(0,0,0,0.5)',
  zIndex: 1500,
};

const menuItemStyles = {
  bg: 'transparent',
  color: 'rgba(255,255,255,0.75)',
  fontSize: 'xs',
  borderRadius: '6px',
  mx: 0.5,
  py: 1.5,
  _hover: { bg: 'rgba(255,255,255,0.05)', color: '#fafafa' },
  _focus: { bg: 'rgba(255,255,255,0.05)' },
};

const dropdownButtonStyles = {
  variant: 'outline',
  w: '100%',
  justifyContent: 'space-between',
  textAlign: 'left',
  fontWeight: 'normal',
  size: 'sm',
  h: '34px',
  bg: '#0a0a0a',
  border: '1px solid',
  borderColor: 'rgba(255,255,255,0.10)',
  fontSize: 'sm',
  borderRadius: '8px',
  _hover: { borderColor: 'rgba(255,255,255,0.2)' },
  _expanded: { borderColor: 'rgba(255,255,255,0.3)', boxShadow: 'none' },
  _focus: { boxShadow: 'none', outline: 'none' },
  _focusVisible: { boxShadow: 'none', outline: 'none' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Follow-up step row
// ─────────────────────────────────────────────────────────────────────────────

const FollowUpStepRow = ({ index, step, emailOptions, onDraftChange, onDelayChange, onRemove }) => {
  const stepNumber = index + 2;

  return (
    <Box
      px={3}
      py={3}
      borderRadius="8px"
      bg="rgba(255,255,255,0.02)"
      border="1px solid rgba(255,255,255,0.08)"
    >
      <HStack justify="space-between" mb={2}>
        <Text color="rgba(255,255,255,0.5)" fontSize="xs" fontWeight="600" letterSpacing="0.03em">
          Step {stepNumber}
        </Text>
        <IconButton
          icon={<LuTrash2 size={13} />}
          size="xs"
          variant="ghost"
          color="rgba(255,255,255,0.25)"
          aria-label={`Remove step ${stepNumber}`}
          _hover={{ color: 'rgba(239,68,68,0.8)', bg: 'rgba(239,68,68,0.08)' }}
          _focus={{ boxShadow: 'none', outline: 'none' }}
          onClick={() => onRemove(index)}
        />
      </HStack>

      <Menu placement="bottom-start">
        <MenuButton
          as={Button}
          rightIcon={<LuChevronDown size={13} />}
          {...dropdownButtonStyles}
          color={step.draft ? '#fafafa' : 'rgba(255,255,255,0.3)'}
        >
          <Text fontSize="xs" noOfLines={1}>
            {step.draft ? step.draft.emailName : 'Select message draft'}
          </Text>
        </MenuButton>
        <MenuList {...menuListStyles}>
          {emailOptions.map((draft) => (
            <MenuItem
              key={draft.id}
              onClick={() => onDraftChange(index, draft)}
              {...menuItemStyles}
            >
              <HStack w="100%" justify="space-between">
                <Text fontSize="xs" noOfLines={1}>{draft.emailName}</Text>
                {step.draft?.id === draft.id && (
                  <Text fontSize="11px" color="rgba(255,255,255,0.85)" fontWeight="600">✓</Text>
                )}
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <HStack mt={2.5} spacing={2} align="center">
        <Text color="rgba(255,255,255,0.35)" fontSize="xs" flexShrink={0}>
          Send after
        </Text>
        <NumberInput
          value={step.delayDays}
          onChange={(_, num) => { if (!isNaN(num) && num >= MIN_FOLLOWUP_DELAY) onDelayChange(index, num); }}
          onBlur={() => {
            if (step.delayDays < MIN_FOLLOWUP_DELAY) onDelayChange(index, MIN_FOLLOWUP_DELAY);
            if (step.delayDays > MAX_FOLLOWUP_DELAY) onDelayChange(index, MAX_FOLLOWUP_DELAY);
          }}
          min={MIN_FOLLOWUP_DELAY}
          max={MAX_FOLLOWUP_DELAY}
          step={1}
          clampValueOnBlur={false}
          size="xs"
          w="70px"
        >
          <NumberInputField
            h="28px"
            bg="rgba(255,255,255,0.04)"
            border="1px solid"
            borderColor="rgba(255,255,255,0.10)"
            borderRadius="6px"
            color="#fafafa"
            fontSize="xs"
            textAlign="center"
            px={2}
            _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
            _focus={{ borderColor: 'rgba(255,255,255,0.3)', boxShadow: 'none' }}
          />
          <NumberInputStepper border="none" w="22px" borderRadius="0 6px 6px 0" overflow="hidden">
            <NumberIncrementStepper
              border="none" bg="transparent" color="rgba(255,255,255,0.25)" fontSize="8px"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
            />
            <NumberDecrementStepper
              border="none" bg="transparent" color="rgba(255,255,255,0.25)" fontSize="8px"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
            />
          </NumberInputStepper>
        </NumberInput>
        <Text color="rgba(255,255,255,0.35)" fontSize="xs">
          day{step.delayDays !== 1 ? 's' : ''}
        </Text>
      </HStack>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Status badge for sequences list
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_BADGE = {
  completed: { bg: 'rgba(34,197,94,0.10)',  border: 'rgba(34,197,94,0.30)',  color: 'rgba(74,222,128,0.85)' },
  active:    { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.65)' },
  paused:    { bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.30)', color: 'rgba(251,191,36,0.85)' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGE[status] || STATUS_BADGE.active;
  return (
    <Box px={1.5} py="2px" borderRadius="full" bg={s.bg} border={`1px solid ${s.border}`} flexShrink={0}>
      <Text fontSize="9px" fontWeight="600" color={s.color} letterSpacing="0.06em" textTransform="capitalize">
        {status}
      </Text>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────

const SequencerExportPanel = ({
  onClose,
  campaignId,
  onExportTrackingRefresh,
  onGillyreachSequencerStatusRefresh,
}) => {
  const navigate = useNavigate();
  const {
    withCreditWarning,
    isWarningOpen,
    warningData,
    handleConfirm: handleCreditConfirm,
    handleOptOut: handleCreditOptOut,
    closeWarning,
    userSubscriptionStatus,
  } = useCreditWarning();

  const [selectedSequencer,     setSelectedSequencer]     = useState(null);
  const [showAddAccountModal,   setShowAddAccountModal]   = useState(false);
  // Flat list of all connected OAuth accounts across Gmail + Outlook
  // Shape: [{providerId: 'gmail'|'outlook', email: string}]
  const [connectedAccounts,     setConnectedAccounts]     = useState([]);
  // Which account the user has highlighted inside the modal
  const [selectedModalAccount,  setSelectedModalAccount]  = useState(null);
  const [addedAccounts,         setAddedAccounts]         = useState([]);
  const [emailOptions,          setEmailOptions]          = useState([]);
  const [isLoadingMessages,     setIsLoadingMessages]     = useState(false);
  const [selectedMessage,       setSelectedMessage]       = useState(null);
  const [sequenceName,          setSequenceName]          = useState('');
  const [followUpSteps,         setFollowUpSteps]         = useState([]);

  const [viewMode,              setViewMode]              = useState('setup');
  const [campaignSequences,     setCampaignSequences]     = useState([]);
  const [loadingSequences,      setLoadingSequences]      = useState(false);
  const [deletingSequenceId,    setDeletingSequenceId]    = useState(null);
  const [togglingSequenceId,    setTogglingSequenceId]    = useState(null);

  const [exportState,   setExportState]   = useState(null);
  const [exportError,   setExportError]   = useState(null);
  const [verifyState,   setVerifyState]   = useState('idle');
  const [verifyProgress, setVerifyProgress] = useState({ verified: 0, total: 0 });
  const [verifyResult,  setVerifyResult]  = useState(null);
  const pollRef   = useRef(null);
  const taskIdRef = useRef(null);

  const { isApproved: isCampaignApproved, complianceStatus: verdictStatus } = useCampaignComplianceStatus(campaignId);

  // ── Follow-up step management ─────────────────────────────────────────────

  const addFollowUpStep    = useCallback(() => {
    setFollowUpSteps(prev => [...prev, { draft: null, delayDays: DEFAULT_FOLLOWUP_DELAY }]);
  }, []);

  const removeFollowUpStep = useCallback((index) => {
    setFollowUpSteps(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateFollowUpDraft = useCallback((index, draft) => {
    setFollowUpSteps(prev => prev.map((s, i) => i === index ? { ...s, draft } : s));
  }, []);

  const updateFollowUpDelay = useCallback((index, delayDays) => {
    setFollowUpSteps(prev => prev.map((s, i) => i === index ? { ...s, delayDays } : s));
  }, []);

  // ── Reset ─────────────────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    setSelectedSequencer(null);
    setAddedAccounts([]);
    setSelectedMessage(null);
    setSequenceName('');
    setFollowUpSteps([]);
    setExportState(null);
    setExportError(null);
    setVerifyState('idle');
    setVerifyProgress({ verified: 0, total: 0 });
    setVerifyResult(null);
    taskIdRef.current = null;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => {
    if (selectedSequencer && !sequenceName) {
      const today = new Date();
      setSequenceName(
        today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      );
    }
  }, [selectedSequencer]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCampaignSequences = useCallback(async () => {
    if (!campaignId) return;
    setLoadingSequences(true);
    try {
      const { data } = await api.get('/api/sequencer/campaign-sequences', {
        params: { campaign_id: campaignId },
      });
      setCampaignSequences(data.sequences || []);
    } catch (err) {
      console.error('[SequencerExportPanel] fetch sequences error:', err);
      setCampaignSequences([]);
    } finally {
      setLoadingSequences(false);
    }
  }, [campaignId]);

  const toggleViewMode = useCallback(() => {
    setViewMode(prev => {
      const next = prev === 'setup' ? 'sequences' : 'setup';
      if (next === 'sequences') fetchCampaignSequences();
      return next;
    });
  }, [fetchCampaignSequences]);

  const handleDeleteSequence = useCallback(async (seqId) => {
    setDeletingSequenceId(seqId);
    try {
      await api.post(`/api/sequencer/${seqId}/cancel`);
      setCampaignSequences(prev => prev.filter(s => s.sequence_id !== seqId));
      await onExportTrackingRefresh?.();
      await new Promise((resolve) => setTimeout(resolve, 0));
      await onGillyreachSequencerStatusRefresh?.();
    } catch (err) {
      console.error('[SequencerExportPanel] delete sequence error:', err);
    } finally {
      setDeletingSequenceId(null);
    }
  }, [onExportTrackingRefresh, onGillyreachSequencerStatusRefresh]);

  const handleTogglePause = useCallback(async (seqId, currentStatus) => {
    const action = currentStatus === 'active' ? 'pause' : 'resume';
    setTogglingSequenceId(seqId);
    try {
      const { data } = await api.post(`/api/sequencer/${seqId}/${action}`);
      setCampaignSequences(prev =>
        prev.map(s => s.sequence_id === seqId ? { ...s, status: data.status } : s)
      );
      await onGillyreachSequencerStatusRefresh?.();
    } catch (err) {
      console.error(`[SequencerExportPanel] ${action} sequence error:`, err);
    } finally {
      setTogglingSequenceId(null);
    }
  }, [onGillyreachSequencerStatusRefresh]);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setVerifyState('idle');
    setVerifyResult(null);
    setVerifyProgress({ verified: 0, total: 0 });
    taskIdRef.current = null;
  }, [
    selectedSequencer?.id,
    JSON.stringify(addedAccounts.map(a => a.providerId)), // eslint-disable-line react-hooks/exhaustive-deps
    selectedMessage?.id,
  ]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Contact verification ──────────────────────────────────────────────────

  const startVerify = useCallback(async () => {
    if (!campaignId) return;
    setVerifyState('running');
    setVerifyProgress({ verified: 0, total: 0 });
    setVerifyResult(null);

    try {
      const { data } = await api.post(`/api/emails/${campaignId}/verify-start`);
      const { task_id, total, status } = data;
      taskIdRef.current = task_id;
      setVerifyProgress({ verified: 0, total: total ?? 0 });

      if (status === 'completed') {
        setVerifyState('done');
        setVerifyResult({ valid: total ?? 0, skipped: 0 });
        return;
      }

      pollRef.current = setInterval(async () => {
        try {
          const { data: progress } = await api.get(
            `/api/emails/${campaignId}/verify-progress?task_id=${task_id}`
          );
          setVerifyProgress({ verified: progress.verified ?? 0, total: progress.total ?? 0 });

          if (progress.status === 'completed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            const r = progress.result ?? {};
            setVerifyResult({
              valid:   r.surviving_contacts ?? progress.verified ?? 0,
              skipped: r.contacts_removed ?? Math.max(0, (progress.total ?? 0) - (progress.verified ?? 0)),
            });
            setVerifyState('done');
            window.dispatchEvent(new Event('contactsEnrichedAndVerified'));
          } else if (progress.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setVerifyState('error');
          }
        } catch (pollErr) {
          if (pollErr?.response?.status === 404) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setVerifyState('error');
          }
        }
      }, 2000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setVerifyState('error');
      if (err?.response?.status === 409 && taskIdRef.current) setVerifyState('running');
      if (detail) console.warn('[SequencerExportPanel] verify-start:', detail);
    }
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerify = useCallback(async () => {
    if (!campaignId) return;
    try {
      await withCreditWarning('email_verification', { campaign_id: campaignId }, startVerify);
    } catch (e) {
      if (!e?.isCancellation) {
        console.error('[SequencerExportPanel] verify credit warning:', e);
      }
    }
  }, [campaignId, withCreditWarning, startVerify]);

  const verifyPct = verifyProgress.total > 0
    ? Math.min(Math.round((verifyProgress.verified / verifyProgress.total) * 100), 100)
    : 0;

  // ── Export ────────────────────────────────────────────────────────────────

  const allFollowUpsReady = followUpSteps.length === 0 || followUpSteps.every(s => s.draft !== null);

  const handleExport = useCallback(async () => {
    if (!campaignId || !selectedMessage || addedAccounts.length === 0 || !taskIdRef.current) return;

    setExportState('loading');
    setExportError(null);

    const account = addedAccounts[0];
    const steps = [
      {
        step: 1,
        message_draft_id:  selectedMessage.id,
        subject_field_id:  selectedMessage.subjectFieldId,
        body_field_id:     selectedMessage.bodyFieldId,
        email_name:        selectedMessage.emailName,
        delay_days:        0,
      },
      ...followUpSteps.map((fu, i) => ({
        step:              i + 2,
        message_draft_id:  fu.draft.id,
        subject_field_id:  fu.draft.subjectFieldId,
        body_field_id:     fu.draft.bodyFieldId,
        email_name:        fu.draft.emailName,
        delay_days:        fu.delayDays,
      })),
    ];

    const payload = {
      campaign_id:          campaignId,
      verification_task_id: taskIdRef.current,
      provider:             account.providerId,
      account_email:        account.email,
      sequence_name:        sequenceName || null,
      steps,
    };

    try {
      const { data } = await api.post('/api/sequencer/export', payload);
      setExportState(data);
      await onExportTrackingRefresh?.();
      await new Promise((resolve) => setTimeout(resolve, 0));
      await onGillyreachSequencerStatusRefresh?.();
    } catch (err) {
      let detail = err?.response?.data?.detail ?? 'Export failed. Please try again.';
      if (Array.isArray(detail)) {
        detail = detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
      } else if (detail !== null && typeof detail === 'object') {
        detail = 'Export failed. Please try again.';
      }
      setExportError(detail);
      setExportState('error');
      console.error('[SequencerExportPanel] export error:', err);
    }
  }, [
    campaignId, selectedMessage, addedAccounts, followUpSteps, sequenceName,
    onExportTrackingRefresh, onGillyreachSequencerStatusRefresh,
  ]);

  // ── Pre-fetch all connected email accounts ────────────────────────────────

  useEffect(() => {
    if (selectedSequencer?.id !== 'gillyreach') return;

    const fetchConnectedAccounts = async () => {
      const [gmailResult, outlookResult] = await Promise.allSettled([
        api.get('/api/integrations/gmail/status'),
        api.get('/api/integrations/microsoft/status'),
      ]);

      const accounts = [];

      if (gmailResult.status === 'fulfilled') {
        for (const a of gmailResult.value.data.accounts ?? []) {
          accounts.push({ providerId: 'gmail', email: a.email });
        }
      }

      if (outlookResult.status === 'fulfilled') {
        for (const a of outlookResult.value.data.accounts ?? []) {
          accounts.push({ providerId: 'outlook', email: a.email });
        }
      }

      setConnectedAccounts(accounts);
    };

    fetchConnectedAccounts();
  }, [selectedSequencer]);

  // ── Eager-fetch email drafts ──────────────────────────────────────────────

  useEffect(() => {
    if (selectedSequencer?.id !== 'gillyreach' || !campaignId) {
      setEmailOptions([]);
      setSelectedMessage(null);
      return;
    }

    let cancelled = false;
    const fetchEmailOptions = async () => {
      setIsLoadingMessages(true);
      try {
        const { data } = await api.get(`/api/message-drafting/campaigns/${campaignId}/drafts`);
        if (cancelled) return;
        const emailDrafts = (data.drafts ?? []).filter(d => d.typeOfMessage === 'email');
        setEmailOptions(emailDrafts);
        setSelectedMessage(prev =>
          prev && emailDrafts.some(d => d.id === prev.id) ? prev : null
        );
      } catch {
        if (!cancelled) setEmailOptions([]);
      } finally {
        if (!cancelled) setIsLoadingMessages(false);
      }
    };

    fetchEmailOptions();
    return () => { cancelled = true; };
  }, [selectedSequencer, campaignId]);

  // ── Render ────────────────────────────────────────────────────────────────

  const totalSteps = 1 + followUpSteps.length;

  return (
    <Box h="100%" display="flex" flexDirection="column" bg="#111111">

      {/* ── Header ─────────────────────────────────────────────── */}
      <HStack
        px={4}
        py={3}
        borderBottom="1px solid rgba(255,255,255,0.10)"
        justify="space-between"
        align="center"
      >
        <HStack spacing={2} flex={1} minW={0} align="center">
          {viewMode === 'sequences' && (
            <IconButton
              icon={<LuChevronLeft size={20} strokeWidth={1.75} />}
              onClick={toggleViewMode}
              variant="unstyled"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="32px"
              h="32px"
              minW="32px"
              color="rgba(255,255,255,0.4)"
              aria-label="Back to setup"
              flexShrink={0}
              cursor="pointer"
              bg="transparent"
              outline="none"
              boxShadow="none"
              _hover={{ color: 'rgba(255,255,255,0.85)', bg: 'transparent' }}
              _active={{ color: 'rgba(255,255,255,0.65)', bg: 'transparent' }}
              _focus={{ boxShadow: 'none', outline: 'none', bg: 'transparent' }}
              _focusVisible={{ boxShadow: 'none', outline: 'none', bg: 'transparent' }}
            />
          )}
          <Text color="#fafafa" fontSize="md" fontWeight="600" letterSpacing="-0.01em" noOfLines={1}>
            {viewMode === 'sequences' ? 'Sequences' : 'Export to Sequencer'}
          </Text>
        </HStack>
        <HStack spacing={1} flexShrink={0}>
          {viewMode === 'setup' && (
            <Tooltip
              label="View sequences"
              placement="bottom"
              openDelay={200}
              bg="#111111"
              color="#fafafa"
              fontSize="xs"
              fontWeight="500"
              px={3}
              py={2}
              borderRadius="6px"
              borderWidth="1px"
              borderColor="rgba(255,255,255,0.10)"
              boxShadow="0 4px 12px rgba(0,0,0,0.4)"
            >
              <IconButton
                icon={<LuList size={16} />}
                onClick={toggleViewMode}
                variant="ghost"
                color="rgba(255,255,255,0.4)"
                size="sm"
                aria-label="View sequences"
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              />
            </Tooltip>
          )}
          <IconButton
            icon={<FiX />}
            onClick={onClose}
            variant="ghost"
            color="rgba(255,255,255,0.5)"
            fontSize="18px"
            size="sm"
            aria-label="Close"
            transition="all 0.2s ease"
            _hover={{ color: 'rgba(239,68,68,0.8)', bg: 'transparent' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
          />
        </HStack>
      </HStack>

      {/* ── Sequences list view ────────────────────────────────── */}
      {viewMode === 'sequences' && (
        <Box flex={1} overflowY="auto" px={4} py={4}>
          {loadingSequences ? (
            <VStack py={10}><Spinner size="sm" color="rgba(255,255,255,0.4)" /></VStack>
          ) : campaignSequences.length === 0 ? (
            <VStack spacing={3} py={10} align="center">
              <Box opacity={0.2}><LuList size={26} color="white" /></Box>
              <VStack spacing={1} align="center">
                <Text color="rgba(255,255,255,0.4)" fontSize="sm" fontWeight="500">
                  No sequences
                </Text>
                <Text color="rgba(255,255,255,0.22)" fontSize="xs" textAlign="center" maxW="200px" lineHeight="1.65">
                  No sequences have been set up for this campaign yet.
                </Text>
              </VStack>
            </VStack>
          ) : (
            <VStack spacing={3} align="stretch">
              {campaignSequences.map((seq) => {
                const createdLabel = seq.created_at
                  ? new Date(seq.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '';
                const canToggle  = seq.status === 'active' || seq.status === 'paused';
                const steps      = seq.step_progress || [];
                const currentStep = steps.find(s => s.sent < s.total) || steps[steps.length - 1];
                const isComplete  = seq.status === 'completed' || (steps.length > 0 && steps.every(s => s.sent >= s.total));
                const progressPct = currentStep && currentStep.total > 0
                  ? Math.round((currentStep.sent / currentStep.total) * 100)
                  : 0;
                const barBg = isComplete
                  ? 'rgba(74,222,128,0.8)'
                  : seq.status === 'paused'
                    ? 'rgba(251,191,36,0.75)'
                    : 'rgba(255,255,255,0.6)';

                return (
                  <Box
                    key={seq.sequence_id}
                    px={3}
                    py={3}
                    borderRadius="8px"
                    bg="rgba(255,255,255,0.02)"
                    border="1px solid rgba(255,255,255,0.08)"
                  >
                    <HStack justify="space-between" mb={1.5}>
                      <Text color="#fafafa" fontSize="sm" fontWeight="500" noOfLines={1} flex={1}>
                        {seq.sequence_name || createdLabel || 'Untitled'}
                      </Text>
                      <StatusBadge status={seq.status} />
                    </HStack>

                    {steps.length > 0 && currentStep && (
                      <Box mb={2}>
                        <HStack justify="space-between" mb={1}>
                          <Text color="rgba(255,255,255,0.45)" fontSize="11px" fontWeight="500">
                            {isComplete
                              ? `All ${steps.length} step${steps.length !== 1 ? 's' : ''} complete`
                              : `Step ${currentStep.step}/${steps.length}`}
                          </Text>
                          <Text color="rgba(255,255,255,0.3)" fontSize="11px" fontVariantNumeric="tabular-nums">
                            {isComplete
                              ? `${steps.reduce((a, s) => a + s.sent, 0)} sent`
                              : `${currentStep.sent}/${currentStep.total}`}
                          </Text>
                        </HStack>
                        <Box h="3px" w="100%" bg="rgba(255,255,255,0.08)" borderRadius="full" overflow="hidden">
                          <Box
                            h="100%"
                            w={isComplete ? '100%' : `${progressPct}%`}
                            bg={barBg}
                            borderRadius="full"
                            transition="width 0.4s ease"
                          />
                        </Box>
                      </Box>
                    )}

                    <HStack spacing={3}>
                      <Text color="rgba(255,255,255,0.3)" fontSize="xs">
                        {seq.total_contacts} contact{seq.total_contacts !== 1 ? 's' : ''}
                      </Text>
                      <Text color="rgba(255,255,255,0.2)" fontSize="xs">&middot;</Text>
                      <Text color="rgba(255,255,255,0.3)" fontSize="xs">
                        {seq.total_steps} step{seq.total_steps !== 1 ? 's' : ''}
                      </Text>
                      <Text color="rgba(255,255,255,0.2)" fontSize="xs">&middot;</Text>
                      <Text color="rgba(255,255,255,0.3)" fontSize="xs" noOfLines={1}>
                        {seq.account_email}
                      </Text>
                    </HStack>

                    <HStack justify="space-between" mt={1.5}>
                      {createdLabel && (
                        <Text color="rgba(255,255,255,0.2)" fontSize="11px">
                          Created {createdLabel}
                        </Text>
                      )}
                      <HStack spacing={0} ml="auto">
                        {canToggle && (
                          <IconButton
                            icon={togglingSequenceId === seq.sequence_id
                              ? <Spinner size="xs" />
                              : seq.status === 'active' ? <LuPause size={15} /> : <LuPlay size={15} />}
                            size="sm"
                            variant="ghost"
                            color={seq.status === 'paused' ? 'rgba(74,222,128,0.8)' : 'rgba(255,255,255,0.4)'}
                            aria-label={seq.status === 'active' ? 'Pause sequence' : 'Resume sequence'}
                            _hover={{
                              color: seq.status === 'active' ? 'rgba(251,191,36,0.8)' : 'rgba(74,222,128,0.8)',
                              bg: seq.status === 'active' ? 'rgba(234,179,8,0.08)' : 'rgba(34,197,94,0.08)',
                            }}
                            _focus={{ boxShadow: 'none', outline: 'none' }}
                            isDisabled={togglingSequenceId !== null}
                            onClick={() => handleTogglePause(seq.sequence_id, seq.status)}
                          />
                        )}
                        <IconButton
                          icon={deletingSequenceId === seq.sequence_id ? <Spinner size="xs" /> : <LuTrash2 size={15} />}
                          size="sm"
                          variant="ghost"
                          color="rgba(255,255,255,0.4)"
                          aria-label="Delete sequence"
                          _hover={{ color: 'rgba(239,68,68,0.8)', bg: 'rgba(239,68,68,0.08)' }}
                          _focus={{ boxShadow: 'none', outline: 'none' }}
                          isDisabled={deletingSequenceId !== null}
                          onClick={() => handleDeleteSequence(seq.sequence_id)}
                        />
                      </HStack>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      )}

      {/* ── Approval gate banner ────────────────────────────────── */}
      {viewMode === 'setup' && !isCampaignApproved && (
        <Box
          px={4}
          py={2.5}
          borderBottom="1px solid"
          borderColor={verdictStatus === 'rejected' ? 'rgba(239,68,68,0.20)' : 'rgba(255,255,255,0.08)'}
          bg={verdictStatus === 'rejected' ? 'rgba(239,68,68,0.05)' : 'rgba(255,255,255,0.02)'}
          flexShrink={0}
        >
          <HStack spacing={2}>
            <Box
              px={2}
              py="2px"
              borderRadius="full"
              bg={verdictStatus === 'rejected' ? 'rgba(239,68,68,0.12)' : 'rgba(251,191,36,0.10)'}
              border={`1px solid ${verdictStatus === 'rejected' ? 'rgba(239,68,68,0.30)' : 'rgba(251,191,36,0.28)'}`}
              flexShrink={0}
            >
              <Text
                fontSize="9px"
                fontWeight="700"
                color={verdictStatus === 'rejected' ? 'rgba(248,113,113,0.9)' : 'rgba(251,191,36,0.9)'}
                letterSpacing="0.06em"
                textTransform="uppercase"
              >
                {verdictStatus === 'rejected' ? 'Rejected' : 'Pending Review'}
              </Text>
            </Box>
            <Text fontSize="xs" color="rgba(255,255,255,0.45)" lineHeight="1.5" flex={1}>
              {verdictStatus === 'rejected'
                ? 'Your reviewer rejected this campaign. Revise and request re-review before exporting.'
                : 'Export is locked until a reviewer approves this campaign.'}
            </Text>
          </HStack>
        </Box>
      )}

      {/* ── Body ───────────────────────────────────────────────── */}
      {viewMode === 'setup' && (
        <Box flex={1} overflowY="auto" px={4} py={4}>
          <VStack spacing={5} align="stretch">

            {/* Sequencer dropdown */}
            <Box>
              <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>
                Sequencer
              </Text>
              <Menu placement="bottom-start">
                <MenuButton
                  as={Button}
                  rightIcon={<LuChevronDown size={14} />}
                  {...dropdownButtonStyles}
                  h="36px"
                  color={selectedSequencer ? '#fafafa' : 'rgba(255,255,255,0.3)'}
                >
                  {selectedSequencer ? (
                    <HStack spacing={2}>
                      <Box as="img" src={selectedSequencer.icon} w="18px" h="18px" borderRadius="4px" flexShrink={0} />
                      <Text fontSize="sm">{selectedSequencer.name}</Text>
                    </HStack>
                  ) : (
                    <Text fontSize="sm">Select a sequencer</Text>
                  )}
                </MenuButton>
                <MenuList {...menuListStyles}>
                  {SEQUENCERS.map((seq) => (
                    <MenuItem
                      key={seq.id}
                      onClick={() => setSelectedSequencer(seq)}
                      {...{ ...menuItemStyles, fontSize: 'sm', py: 2 }}
                    >
                      <HStack spacing={2} w="100%" justify="space-between">
                        <HStack spacing={2}>
                          <Box as="img" src={seq.icon} w="18px" h="18px" borderRadius="4px" flexShrink={0} />
                          <Text fontSize="sm">{seq.name}</Text>
                        </HStack>
                        {selectedSequencer?.id === seq.id && (
                          <Text fontSize="12px" color="rgba(255,255,255,0.85)" fontWeight="600">✓</Text>
                        )}
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>

            {/* Sequence name */}
            {selectedSequencer?.id === 'gillyreach' && (
              <Box>
                <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>
                  Sequence name
                </Text>
                <Input
                  value={sequenceName}
                  onChange={(e) => setSequenceName(e.target.value)}
                  placeholder="e.g. Q1 Outreach"
                  size="sm"
                  h="36px"
                  bg="#0a0a0a"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.10)"
                  color="#fafafa"
                  fontSize="sm"
                  borderRadius="8px"
                  _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                  _focus={{ borderColor: 'rgba(255,255,255,0.3)', boxShadow: 'none' }}
                  _placeholder={{ color: 'rgba(255,255,255,0.2)' }}
                />
              </Box>
            )}

            {/* Email accounts */}
            {selectedSequencer?.id === 'gillyreach' && (
              <Box>
                <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>
                  Email accounts
                </Text>
                <VStack spacing={3} align="stretch">
                  <Text color="rgba(255,255,255,0.35)" fontSize="xs" lineHeight="1.5">
                    Manage all your accounts{' '}
                    <Text
                      as="span"
                      color="rgba(255,255,255,0.65)"
                      cursor="pointer"
                      fontWeight="500"
                      textDecoration="underline"
                      textDecorationColor="rgba(255,255,255,0.25)"
                      _hover={{ color: '#fafafa', textDecorationColor: 'rgba(255,255,255,0.55)' }}
                      onClick={() => navigate('/integrations')}
                    >
                      here
                    </Text>
                    .
                  </Text>
                  {addedAccounts.map((acc) => {
                    const ProviderIcon = acc.providerId === 'outlook' ? OutlookLogo : GmailLogo;
                    return (
                      <Box
                        key={acc.providerId}
                        display="flex"
                        alignItems="center"
                        gap={3}
                        px={3}
                        h="36px"
                        borderRadius="8px"
                        bg="rgba(255,255,255,0.02)"
                        border="1px solid rgba(255,255,255,0.08)"
                      >
                        <Box flexShrink={0}><ProviderIcon size={20} /></Box>
                        <Text color="#fafafa" fontSize="sm" fontWeight="500" flex={1}>{acc.name}</Text>
                        <Text color="rgba(255,255,255,0.4)" fontSize="xs" noOfLines={1} title={acc.email}>
                          {acc.email}
                        </Text>
                        <Menu placement="bottom-end">
                          <MenuButton
                            as={IconButton}
                            icon={<LuMoreVertical size={16} />}
                            variant="ghost"
                            size="sm"
                            aria-label="Account options"
                            color="rgba(255,255,255,0.35)"
                            _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                            _focus={{ boxShadow: 'none', outline: 'none' }}
                            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                          />
                          <MenuList {...{ ...menuListStyles, minW: '140px' }}>
                            <MenuItem
                              {...menuItemStyles}
                              onClick={() => { setSelectedModalAccount(null); setShowAddAccountModal(true); }}
                            >
                              <HStack spacing={2}>
                                <LuArrowLeftRight size={12} />
                                <Text fontSize="xs">Change account</Text>
                              </HStack>
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Box>
                    );
                  })}
                  {addedAccounts.length === 0 && (
                    <Button
                      leftIcon={<LuPlus size={14} />}
                      size="sm"
                      variant="outline"
                      w="100%"
                      justifyContent="center"
                      h="36px"
                      bg="transparent"
                      borderColor="rgba(255,255,255,0.10)"
                      color="rgba(255,255,255,0.45)"
                      fontSize="sm"
                      fontWeight="normal"
                      borderRadius="8px"
                      _hover={{ bg: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)' }}
                      _focus={{ boxShadow: 'none', outline: 'none' }}
                      onClick={() => { setSelectedModalAccount(null); setShowAddAccountModal(true); }}
                    >
                      Select account
                    </Button>
                  )}
                </VStack>
              </Box>
            )}

            {/* Message draft (Step 1) */}
            {selectedSequencer?.id === 'gillyreach' && (
              <Box>
                <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>
                  Message draft
                </Text>
                <Menu placement="bottom-start">
                  <MenuButton
                    as={Button}
                    rightIcon={isLoadingMessages ? <Spinner size="xs" color="rgba(255,255,255,0.4)" /> : <LuChevronDown size={14} />}
                    {...dropdownButtonStyles}
                    h="36px"
                    color={selectedMessage ? '#fafafa' : 'rgba(255,255,255,0.3)'}
                    isDisabled={isLoadingMessages || emailOptions.length === 0}
                    _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    <Text fontSize="sm" noOfLines={1}>
                      {isLoadingMessages
                        ? 'Loading\u2026'
                        : selectedMessage
                          ? selectedMessage.emailName
                          : emailOptions.length === 0
                            ? 'No email drafts found'
                            : 'Select a message'}
                    </Text>
                  </MenuButton>
                  <MenuList {...menuListStyles}>
                    {emailOptions.map((draft) => (
                      <MenuItem
                        key={draft.id}
                        onClick={() => setSelectedMessage(draft)}
                        {...{ ...menuItemStyles, fontSize: 'sm', py: 2 }}
                      >
                        <HStack w="100%" justify="space-between">
                          <Text fontSize="sm" noOfLines={1}>{draft.emailName}</Text>
                          {selectedMessage?.id === draft.id && (
                            <Text fontSize="12px" color="rgba(255,255,255,0.85)" fontWeight="600">✓</Text>
                          )}
                        </HStack>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Box>
            )}

            {/* Follow-up emails */}
            {selectedSequencer?.id === 'gillyreach' && !!selectedMessage && (
              <Box>
                <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={1}>
                  Follow-up emails
                </Text>
                <Text color="rgba(255,255,255,0.35)" fontSize="xs" lineHeight="1.55" mb={3}>
                  Add follow-up messages sent automatically after the initial email.
                  Each follow-up is sent as a separate, independent email.
                </Text>

                <VStack spacing={2} align="stretch">
                  {followUpSteps.map((step, index) => (
                    <FollowUpStepRow
                      key={index}
                      index={index}
                      step={step}
                      emailOptions={emailOptions}
                      onDraftChange={updateFollowUpDraft}
                      onDelayChange={updateFollowUpDelay}
                      onRemove={removeFollowUpStep}
                    />
                  ))}

                  <Button
                    leftIcon={<LuPlus size={13} />}
                    size="sm"
                    variant="outline"
                    w="100%"
                    justifyContent="center"
                    h="34px"
                    bg="transparent"
                    borderColor="rgba(255,255,255,0.10)"
                    borderStyle="dashed"
                    color="rgba(255,255,255,0.3)"
                    fontSize="xs"
                    fontWeight="normal"
                    borderRadius="8px"
                    _hover={{ bg: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    onClick={addFollowUpStep}
                  >
                    Add follow-up
                  </Button>
                </VStack>
              </Box>
            )}

            {/* Sending schedule note */}
            {selectedSequencer?.id === 'gillyreach' && (
              <Text color="rgba(255,255,255,0.35)" fontSize="xs" lineHeight="1.55">
                All emails are sent Mon&ndash;Fri, 9&nbsp;AM&ndash;5&nbsp;PM ET, optimized for inbox placement and sender health.
              </Text>
            )}

            {/* Contact verification */}
            {selectedSequencer?.id === 'gillyreach' && addedAccounts.length > 0 && selectedMessage && (
              <Box>
                <HStack justify="space-between" mb={1} align="center">
                  <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500">
                    Contact verification
                  </Text>
                  {verifyState === 'done' && (
                    <HStack spacing={1}>
                      <LuShieldCheck size={12} color="rgba(74,222,128,0.85)" />
                      <Text fontSize="xs" color="rgba(74,222,128,0.85)" fontWeight="500">Verified</Text>
                    </HStack>
                  )}
                </HStack>
                <Text color="rgba(255,255,255,0.35)" fontSize="xs" lineHeight="1.55" mb={3}>
                  Confirms deliverable addresses via ZeroBounce before sending. Invalid emails are excluded from the sequence.
                </Text>

                {verifyState === 'idle' && (
                  <Button
                    leftIcon={<LuShieldCheck size={13} />}
                    size="sm"
                    variant="outline"
                    w="100%"
                    h="36px"
                    justifyContent="center"
                    bg="transparent"
                    borderColor="rgba(255,255,255,0.10)"
                    color="rgba(255,255,255,0.45)"
                    fontSize="sm"
                    fontWeight="normal"
                    borderRadius="8px"
                    _hover={{ bg: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    onClick={handleVerify}
                  >
                    Enrich and verify
                  </Button>
                )}

                {verifyState === 'running' && (
                  <Box px={3} py={3} borderRadius="8px" bg="rgba(255,255,255,0.02)" border="1px solid rgba(255,255,255,0.08)">
                    <HStack justify="space-between" mb={2.5}>
                      <HStack spacing={2}>
                        <Spinner size="xs" color="rgba(255,255,255,0.5)" speed="0.8s" />
                        <Text fontSize="xs" color="rgba(255,255,255,0.45)">{'Verifying addresses\u2026'}</Text>
                      </HStack>
                      <Text fontSize="xs" color="#fafafa" fontWeight="500" fontVariantNumeric="tabular-nums">
                        {verifyProgress.verified.toLocaleString()}&thinsp;/&thinsp;{verifyProgress.total.toLocaleString()}
                      </Text>
                    </HStack>
                    <Box h="3px" bg="rgba(255,255,255,0.08)" borderRadius="full" overflow="hidden">
                      <Box
                        h="100%"
                        bg="rgba(255,255,255,0.65)"
                        borderRadius="full"
                        w={`${verifyPct}%`}
                        transition="width 0.5s ease"
                      />
                    </Box>
                    <Text fontSize="10px" color="rgba(255,255,255,0.25)" mt={1.5} textAlign="right">
                      {verifyPct}%
                    </Text>
                  </Box>
                )}

                {verifyState === 'done' && verifyResult && (
                  <Box px={3} py={2.5} borderRadius="8px" bg="rgba(34,197,94,0.06)" border="1px solid rgba(34,197,94,0.18)">
                    <HStack spacing={4}>
                      <HStack spacing={1.5}>
                        <Text fontSize="sm" color="rgba(74,222,128,0.85)" fontWeight="600" fontVariantNumeric="tabular-nums">
                          {verifyResult.valid.toLocaleString()}
                        </Text>
                        <Text fontSize="xs" color="rgba(255,255,255,0.4)">valid</Text>
                      </HStack>
                      <Text fontSize="xs" color="rgba(255,255,255,0.15)">&middot;</Text>
                      <HStack spacing={1.5}>
                        <Text fontSize="sm" color="rgba(255,255,255,0.35)" fontWeight="600" fontVariantNumeric="tabular-nums">
                          {verifyResult.skipped.toLocaleString()}
                        </Text>
                        <Text fontSize="xs" color="rgba(255,255,255,0.2)">skipped</Text>
                      </HStack>
                    </HStack>
                  </Box>
                )}

                {verifyState === 'error' && (
                  <Box px={3} py={2.5} borderRadius="8px" bg="rgba(239,68,68,0.06)" border="1px solid rgba(239,68,68,0.2)">
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="rgba(239,68,68,0.8)" lineHeight="1.5">
                        Verification failed. Please try again.
                      </Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="rgba(255,255,255,0.4)"
                        fontSize="xs"
                        px={2}
                        h="auto"
                        py={1}
                        onClick={() => setVerifyState('idle')}
                        _hover={{ color: 'rgba(255,255,255,0.85)', bg: 'rgba(255,255,255,0.05)' }}
                        _focus={{ boxShadow: 'none', outline: 'none' }}
                      >
                        Retry
                      </Button>
                    </HStack>
                  </Box>
                )}
              </Box>
            )}

            {/* Empty state */}
            {!selectedSequencer && (
              <VStack spacing={3} py={10} align="center">
                <Box opacity={0.2}><LuSend size={26} color="white" /></Box>
                <VStack spacing={1} align="center">
                  <Text color="rgba(255,255,255,0.4)" fontSize="sm" fontWeight="500" letterSpacing="0.01em">
                    Ready to export
                  </Text>
                  <Text color="rgba(255,255,255,0.22)" fontSize="xs" textAlign="center" maxW="190px" lineHeight="1.65">
                    Save your message drafts, then export them to GillyReach to launch your sequence.
                  </Text>
                </VStack>
              </VStack>
            )}

          </VStack>
        </Box>
      )}

      {/* ── Export success card ──────────────────────────────────── */}
      {viewMode === 'setup' && exportState && exportState !== 'loading' && exportState !== 'error' && (
        <Box px={4} pb={2}>
          <Box p={3} borderRadius="8px" bg="rgba(34,197,94,0.06)" border="1px solid rgba(34,197,94,0.18)">
            <HStack justify="space-between" mb={1}>
              <Text fontSize="xs" color="rgba(74,222,128,0.85)" fontWeight="600" letterSpacing="0.04em" textTransform="uppercase">
                Sequence queued
              </Text>
              <Text fontSize="xs" color="rgba(255,255,255,0.35)" fontVariantNumeric="tabular-nums">
                {exportState.total_contacts?.toLocaleString()} contacts &middot; {exportState.total_steps ?? totalSteps} step{(exportState.total_steps ?? totalSteps) !== 1 ? 's' : ''}
              </Text>
            </HStack>
            <Text fontSize="xs" color="rgba(255,255,255,0.4)" lineHeight="1.6">
              {'Sending starts within the next business hour (Mon\u2013Fri, 9 AM\u20135 PM ET).'}
            </Text>
          </Box>
        </Box>
      )}

      {/* ── Export error card ─────────────────────────────────────── */}
      {viewMode === 'setup' && exportState === 'error' && exportError && (
        <Box px={4} pb={2}>
          <Box p={3} borderRadius="8px" bg="rgba(239,68,68,0.06)" border="1px solid rgba(239,68,68,0.2)">
            <HStack justify="space-between" align="flex-start">
              <Text fontSize="xs" color="rgba(239,68,68,0.8)" lineHeight="1.5" flex={1}>
                {exportError}
              </Text>
              <Button
                size="xs"
                variant="ghost"
                color="rgba(255,255,255,0.4)"
                onClick={() => { setExportState(null); setExportError(null); }}
                _hover={{ color: 'rgba(255,255,255,0.85)' }}
                _focus={{ boxShadow: 'none' }}
                flexShrink={0}
              >
                Retry
              </Button>
            </HStack>
          </Box>
        </Box>
      )}

      {/* ── Footer ─────────────────────────────────────────────── */}
      {viewMode === 'setup' && (
        <HStack
          px={4}
          py={3}
          borderTop="1px solid rgba(255,255,255,0.10)"
          justify="flex-end"
          spacing={2}
        >
          {exportState && exportState !== 'loading' && exportState !== 'error' ? (
            <Button {...outlineBtn} onClick={handleReset}>
              Reset
            </Button>
          ) : (
            <>
              <Button {...outlineBtn} onClick={onClose}>
                Cancel
              </Button>
              <Tooltip
                label={!isCampaignApproved ? 'Campaign must be approved before exporting' : ''}
                isDisabled={isCampaignApproved}
                placement="top"
                bg="#111111"
                color="#fafafa"
                fontSize="xs"
                borderRadius="6px"
                px={2}
                py={1}
                borderWidth="1px"
                borderColor="rgba(255,255,255,0.10)"
              >
                <Button
                  {...primaryBtn}
                  leftIcon={exportState === 'loading' ? <Spinner size="xs" color="#0a0a0a" /> : <LuSend size={13} />}
                  isDisabled={
                    !isCampaignApproved ||
                    !selectedSequencer ||
                    addedAccounts.length === 0 ||
                    !selectedMessage ||
                    verifyState !== 'done' ||
                    !allFollowUpsReady ||
                    exportState === 'loading' ||
                    (exportState && exportState !== 'error')
                  }
                  isLoading={exportState === 'loading'}
                  loadingText="Starting..."
                  onClick={handleExport}
                >
                  Start sequence
                </Button>
              </Tooltip>
            </>
          )}
        </HStack>
      )}

      {/* Credit warning modal */}
      <CreditWarningModal
        isOpen={isWarningOpen}
        onClose={closeWarning}
        operationType={warningData?.operation_type}
        creditsNeeded={warningData?.credits_needed}
        currentBalance={warningData?.balance}
        onConfirm={handleCreditConfirm}
        onOptOut={() => handleCreditOptOut(warningData?.operation_type)}
        userSubscriptionStatus={userSubscriptionStatus}
        allowsOverageProceed={warningData?.preview_allows_overage_proceed === true}
        overageCreditsForRequest={warningData?.overage_credits_for_request}
        overagePricePerCreditCents={warningData?.overage_price_per_credit_cents}
      />

      {/* Select email account modal */}
      <Modal
        isOpen={showAddAccountModal}
        onClose={() => { setShowAddAccountModal(false); setSelectedModalAccount(null); }}
        isCentered
      >
        <ModalOverlay bg="rgba(0,0,0,0.85)" />
        <ModalContent
          bg="#111111"
          border="1px solid rgba(255,255,255,0.10)"
          borderRadius="10px"
          maxW="400px"
          mx={4}
          boxShadow="0 8px 32px rgba(0,0,0,0.5)"
        >
          <ModalHeader
            py={4}
            px={5}
            borderBottom="1px solid rgba(255,255,255,0.08)"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text color="#fafafa" fontSize="md" fontWeight="600" letterSpacing="-0.01em">
              Select sending account
            </Text>
            <IconButton
              icon={<FiX />}
              onClick={() => { setShowAddAccountModal(false); setSelectedModalAccount(null); }}
              variant="ghost"
              color="rgba(255,255,255,0.4)"
              size="sm"
              aria-label="Close"
              borderRadius="full"
              _hover={{ color: 'rgba(255,255,255,0.75)', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
            />
          </ModalHeader>
          <ModalBody py={4} px={5}>
            {connectedAccounts.length === 0 ? (
              <VStack spacing={3} py={6} align="center">
                <Text color="rgba(255,255,255,0.35)" fontSize="sm" textAlign="center" lineHeight="1.6">
                  No Gmail or Outlook accounts connected.
                </Text>
                <Button
                  {...outlineBtn}
                  h="32px"
                  onClick={() => {
                    setShowAddAccountModal(false);
                    navigate('/integrations');
                  }}
                >
                  Connect an account
                </Button>
              </VStack>
            ) : (
              <VStack spacing={2} align="stretch">
                {connectedAccounts.map((acc) => {
                  const Icon = acc.providerId === 'gmail' ? GmailLogo : OutlookLogo;
                  const key = `${acc.providerId}:${acc.email}`;
                  const isSelected = selectedModalAccount?.email === acc.email && selectedModalAccount?.providerId === acc.providerId;
                  return (
                    <Box
                      key={key}
                      w="100%"
                      px={3}
                      py={2.5}
                      borderRadius="8px"
                      border="1px solid"
                      borderColor={isSelected ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.10)'}
                      bg={isSelected ? 'rgba(255,255,255,0.05)' : 'transparent'}
                      display="flex"
                      alignItems="center"
                      gap={3}
                      cursor="pointer"
                      transition="border-color 0.15s, background 0.15s"
                      _hover={{
                        borderColor: isSelected ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
                        bg: isSelected ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.02)',
                      }}
                      onClick={() => setSelectedModalAccount(acc)}
                    >
                      <Box
                        w="8px" h="8px" borderRadius="full" border="2px solid"
                        borderColor={isSelected ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.2)'}
                        bg="transparent"
                        flexShrink={0}
                      />
                      <Box flexShrink={0}><Icon size={20} /></Box>
                      <Text color="#fafafa" fontSize="sm" fontFamily="mono" flex={1} noOfLines={1}>
                        {acc.email}
                      </Text>
                    </Box>
                  );
                })}
                <Text
                  fontSize="xs"
                  color="rgba(255,255,255,0.35)"
                  cursor="pointer"
                  _hover={{ color: 'rgba(255,255,255,0.65)' }}
                  pt={1}
                  onClick={() => {
                    setShowAddAccountModal(false);
                    navigate('/integrations');
                  }}
                >
                  + Connect another account on the Integrations page
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid rgba(255,255,255,0.08)" py={3} px={5} gap={2}>
            <Button
              {...outlineBtn}
              h="32px"
              onClick={() => { setShowAddAccountModal(false); setSelectedModalAccount(null); }}
            >
              Cancel
            </Button>
            <Button
              {...primaryBtn}
              h="32px"
              isDisabled={!selectedModalAccount}
              onClick={() => {
                if (selectedModalAccount) {
                  const label = selectedModalAccount.providerId === 'gmail' ? 'Gmail' : 'Outlook';
                  setAddedAccounts([{
                    providerId: selectedModalAccount.providerId,
                    name:       label,
                    email:      selectedModalAccount.email,
                  }]);
                }
                setShowAddAccountModal(false);
                setSelectedModalAccount(null);
              }}
            >
              Use this account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default SequencerExportPanel;
